// src/components/social/MessageForm.jsx
import React, { useState, useContext } from 'react';
import { FaPaperPlane, FaImage, FaTimes } from 'react-icons/fa';
import { MessageContext } from '../../context/messageContext';

const MessageForm = ({ recipientId, onMessageSent }) => {
  const [content, setContent] = useState('');
  const [media, setMedia] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  const { sendMessage } = useContext(MessageContext);

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMedia(file);
      
      // Create a preview URL
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreviewUrl(fileReader.result);
      };
      fileReader.readAsDataURL(file);
    }
  };

  // Clear selected media
  const clearMedia = () => {
    setMedia(null);
    setPreviewUrl(null);
  };

  // Handle message submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!content.trim() && !media) return;
    
    setIsSubmitting(true);
    try {
      await sendMessage(recipientId, content, media);
      
      // Clear form after successful send
      setContent('');
      setMedia(null);
      setPreviewUrl(null);
      
      if (onMessageSent) {
        onMessageSent();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="relative">
      {media && previewUrl && (
        <div className="mb-2 relative">
          <div className="relative rounded-lg overflow-hidden border border-gray-200">
            <img 
              src={previewUrl} 
              alt="Selected media" 
              className="max-h-40 max-w-full object-contain"
            />
            
            <button
              type="button"
              onClick={clearMedia}
              className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-gray-800 bg-opacity-70 rounded-full text-white hover:bg-opacity-100 transition-colors"
            >
              <FaTimes size={12} />
            </button>
          </div>
        </div>
      )}
      
      <div className="flex items-center border rounded-full overflow-hidden bg-gray-50 pr-2">
        <textarea
          rows="1"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Type a message..."
          disabled={isSubmitting}
          className="flex-1 py-2 px-4 bg-transparent border-none focus:outline-none focus:ring-0 resize-none"
          style={{ minHeight: '44px', maxHeight: '120px' }}
        />
        
        <div className="flex items-center gap-2">
          <label className="cursor-pointer text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-200 transition-colors">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={isSubmitting}
              className="hidden"
            />
            <FaImage />
          </label>
          
          <button 
            type="submit" 
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isSubmitting || (!content.trim() && !media)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-opacity-90'
            }`}
            disabled={isSubmitting || (!content.trim() && !media)}
          >
            <FaPaperPlane size={14} />
          </button>
        </div>
      </div>
    </form>
  );
};

export default MessageForm;